import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { isNil } from 'lodash';
import { SUBJECTS_SLUGS_TYPE, LessonInfo, PrepLessons, Text } from 'platform-components';
import { DEFAULT_PAGE_TO_LOAD, POPUP_DIRECTIONS } from 'platform-components/constants';
import { withRouterPath, InjectedProps as WithRouterPathProps } from 'platform-components/hocs';
import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { bindActionCreators, compose, Dispatch } from 'redux';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { actions as lessonActions } from 'common.components/lesson';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import {
    mapLessonsToIds,
    getPrepLessonsWithoutDetailsIds,
    getShouldLoadPrepLessonsDetails,
} from 'common.components/lesson/utils';
import withTime, { InjectedProps as WithNowProps } from 'common.components/timer/withTime';
import Usage from 'common.components/usage/usage';
import { prepLessonsActions } from 'store/prep-lessons/actions';
import {
    getPrepLessonsDetails,
    getPrepLessonsWithDetails,
    getCanLoadMorePrepLessons,
    getLastPrepLessonsPageLoaded,
    getPrepLessonsLoadingStatus,
    getPrepLessonsDetailsLoadingStatus,
} from 'store/prep-lessons/selectors';
import { PrepLessonDetailsClientSide } from 'store/prep-lessons/typings';
import hasScrolledToElement from 'utils/has-scrolled-to-element';
import { getIsRepeatable } from 'utils/lesson';
import { getIsDeadlinePassed } from 'utils/lesson/dates/dates';

import { RootAction, ReduxTeacherStore } from '../../../typings';
import './theme-page__prep-lessons.scss';

interface OwnProps extends WithRouterPathProps, WithNowProps {}

interface StoreProps {
    canLoadMoreLessons: boolean;
    courseId: number | null;
    lastPageLoaded?: number;
    lessons?: LessonInfo[];
    prepLessonsIds: number[];
    lessonsLoadingStatus: LOADING_STATUSES;
    themeId: number;
    subject: SUBJECTS_SLUGS_TYPE;
    prepLessonsDetails: Record<number, PrepLessonDetailsClientSide>;
    lessonsDetailsLoadingStatus: LOADING_STATUSES;
}

interface DispatchProps {
    clearPrepLessonsDetails: typeof prepLessonsActions.clearPrepLessonsDetails;
    loadLessonViewData: typeof lessonViewActions.loadLessonViewData;
    loadLessonsRequest: typeof prepLessonsActions.loadPrepLessonsRequest;
    loadPrepLessonsDetailsRequest: typeof prepLessonsActions.loadPrepLessonsDetailsRequest;
    toggleLessonModal: (shouldShowModal: boolean) => void;
    clearEditor: typeof lessonActions.clearEditor;
}

interface Props extends OwnProps, StoreProps, DispatchProps {}

// eslint-disable-next-line @typescript-eslint/ban-types
export class ThemePagePrepLessons extends React.Component<Props, {}> {
    componentDidMount() {
        // при навигации по темам происходит unmount/mount
        const { lessons, canLoadMoreLessons } = this.props;

        if (!lessons || lessons.length === 0) {
            this.loadLessons();
        }

        this.loadPrepLessonsDetails();

        if (canLoadMoreLessons) {
            this.bindScroll();
        }
    }

    // eslint-disable-next-line complexity
    componentDidUpdate(prevProps: Props) {
        const {
            lessons: prevLessons,
            themeId: prevThemeId,
            canLoadMoreLessons: prevCanLoadMoreLessons,
            lessonsLoadingStatus: prevLessonsLoadingStatus,
        } = prevProps;
        const { themeId, canLoadMoreLessons, lessons, lessonsLoadingStatus } = this.props;
        const themeIsChanged = prevThemeId !== themeId;

        if (themeIsChanged && (!lessons || lessons.length === 0)) {
            this.loadLessons();
        }

        if (prevCanLoadMoreLessons && !canLoadMoreLessons) {
            this.unbindScroll();
        }

        if (!prevCanLoadMoreLessons && canLoadMoreLessons) {
            this.bindScroll();
        }

        if (
            prevLessonsLoadingStatus === LOADING_STATUSES.LOADING &&
            lessonsLoadingStatus === LOADING_STATUSES.SUCCESS &&
            canLoadMoreLessons
        ) {
            this.tryLoadNextLessonsPage();
        }

        if (prevLessons !== lessons) {
            this.loadPrepLessonsDetails();
        }
    }

    componentWillUnmount() {
        const { clearPrepLessonsDetails } = this.props;

        clearPrepLessonsDetails();
        this.unbindScroll();
    }

    lastPrepLesson: Element;

    bindScroll = () => {
        window.addEventListener('scroll', this.tryLoadNextLessonsPage, { passive: true });
        window.addEventListener('resize', this.tryLoadNextLessonsPage, { passive: true });
    };

    unbindScroll = () => {
        // @ts-ignore
        window.removeEventListener('scroll', this.tryLoadNextLessonsPage, { passive: true });
        // @ts-ignore
        window.removeEventListener('resize', this.tryLoadNextLessonsPage, { passive: true });
    };

    loadLessons = () => {
        const { themeId, loadLessonsRequest, lessonsLoadingStatus } = this.props;

        if (lessonsLoadingStatus !== LOADING_STATUSES.LOADING) {
            loadLessonsRequest({ themeId, page: DEFAULT_PAGE_TO_LOAD });
        }
    };

    loadPrepLessonsDetails = () => {
        const {
            courseId,
            loadPrepLessonsDetailsRequest,
            prepLessonsIds,
            prepLessonsDetails,
            lessonsDetailsLoadingStatus,
        } = this.props;

        if (prepLessonsIds.length > 0 && courseId) {
            const lessonWithoutDetailsIds = getPrepLessonsWithoutDetailsIds(prepLessonsIds, prepLessonsDetails);
            const shouldLoadPrepLessonsDetails = getShouldLoadPrepLessonsDetails(
                lessonWithoutDetailsIds.length,
                lessonsDetailsLoadingStatus
            );

            if (shouldLoadPrepLessonsDetails) {
                loadPrepLessonsDetailsRequest({
                    courseId,
                    lessonsIds: lessonWithoutDetailsIds,
                });
            }
        }
    };

    tryLoadNextLessonsPage = () => {
        const isScrolledIntoView = hasScrolledToElement(this.lastPrepLesson);
        const { lessonsLoadingStatus } = this.props;

        if (isScrolledIntoView && lessonsLoadingStatus !== LOADING_STATUSES.LOADING) {
            this.loadNextStacksPage();
        }
    };

    loadNextStacksPage = () => {
        const { themeId, loadLessonsRequest, lastPageLoaded } = this.props;

        const pageToLoad = isNil(lastPageLoaded) ? DEFAULT_PAGE_TO_LOAD : lastPageLoaded + 1;

        loadLessonsRequest({ themeId, page: pageToLoad });
    };

    setLastPrepLessonRef = (ref: HTMLDivElement | null, index: number) => {
        const { lessons } = this.props;

        if (ref && lessons && index === lessons.length - 1) {
            this.lastPrepLesson = ReactDOM.findDOMNode(ref) as Element;
        }
    };

    handleClick = (lessonId: number) => {
        const { loadLessonViewData, courseId, subject, toggleLessonModal, themeId, clearEditor } = this.props;

        clearEditor();
        loadLessonViewData({
            lessonId,
            courseId,
        });
        toggleLessonModal(true);

        ym('reachGoal', 'teacher_lesson_creation_readylesson_click', {
            subject,
            lessonId,
            themeId,
        });
    };

    renderAssignedWarning = (isRepeatable: boolean, assignedCount?: number): React.ReactNode | null => {
        const shouldShowAssignedWarning = Boolean(assignedCount && assignedCount > 0 && isRepeatable);

        if (!shouldShowAssignedWarning) {
            return null;
        }

        return (
            <Usage
                mix="theme-page__prep-lesson-item-usage"
                popupDirection={POPUP_DIRECTIONS.TOP}
                tooltipMix="theme-page__prep-lesson-item-usage-tooltip"
            >
                <Text id="library.prepLessons.assignedWarning" type={Text.TYPE.MARKDOWN} />
            </Usage>
        );
    };

    render() {
        const { lessons, lessonsLoadingStatus, now } = this.props;
        const isLoading = lessonsLoadingStatus === LOADING_STATUSES.LOADING;

        return (
            <PrepLessons
                isLoading={isLoading}
                mix="theme-page__prep-lessons"
                shouldShowSkeletonLoader={true}
                theme={PrepLessons.THEMES.GRID}
            >
                {lessons &&
                    lessons.map((lesson, index) => {
                        const { id, meta, assignedCount } = lesson;
                        const { accessible_to_teacher: accessibleToTeacher, date_deadline: dateDeadline } = meta;
                        const isRepeatable = getIsRepeatable(meta);

                        const isDateDeadlinedPassed = getIsDeadlinePassed(
                            dateDeadline ? new Date(dateDeadline) : null,
                            now
                        );
                        const canView = !isDateDeadlinedPassed;

                        return (
                            <PrepLessons.Item
                                assignedWarning={this.renderAssignedWarning(isRepeatable, assignedCount)}
                                buttonTextId="library.prepLessons.lookAndAssign"
                                disabled={!canView}
                                endDate={dateDeadline ? new Date(dateDeadline) : null}
                                index={index}
                                key={id}
                                lesson={lesson}
                                onClick={this.handleClick}
                                onLastPrepLessonRef={this.setLastPrepLessonRef}
                                shouldShowButton={true}
                                startDate={accessibleToTeacher ? new Date(accessibleToTeacher) : null}
                            />
                        );
                    })}
            </PrepLessons>
        );
    }
}

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: OwnProps): StoreProps => {
    const {
        match: {
            params: { themeId: thId, subject },
        },
    } = ownProps;
    const themeId = Number(thId);
    const courseId = getCurrentYearCourseId(storeState);
    const prepLessons = getPrepLessonsWithDetails(storeState, {
        themeId,
        courseId,
    });
    const prepLessonsIds = mapLessonsToIds(prepLessons);

    return {
        canLoadMoreLessons: getCanLoadMorePrepLessons(storeState, { themeId }),
        courseId,
        lastPageLoaded: getLastPrepLessonsPageLoaded(storeState, { themeId }),
        lessons: prepLessons,
        prepLessonsIds,
        lessonsLoadingStatus: getPrepLessonsLoadingStatus(storeState, { themeId }),
        lessonsDetailsLoadingStatus: getPrepLessonsDetailsLoadingStatus(storeState, {
            courseId,
            lessonsIds: prepLessonsIds,
        }),
        prepLessonsDetails: getPrepLessonsDetails(storeState, { courseId }),
        themeId,
        subject,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>): DispatchProps => {
    return bindActionCreators(
        {
            loadLessonsRequest: prepLessonsActions.loadPrepLessonsRequest,
            loadPrepLessonsDetailsRequest: prepLessonsActions.loadPrepLessonsDetailsRequest,
            clearPrepLessonsDetails: prepLessonsActions.clearPrepLessonsDetails,
            toggleLessonModal: lessonViewActions.toggleModal,
            loadLessonViewData: lessonViewActions.loadLessonViewData,
            clearEditor: lessonActions.clearEditor,
        },
        dispatch
    );
};

export default compose<any>(
    withRouterPath,
    withTime,
    connect(mapStateToProps, mapDispatchToProps)
)(ThemePagePrepLessons);
